import {
  Box,
  Button,
  HStack,
  Input,
  NativeSelect,
  Separator,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import {
  Checkbox,
  Field,
  Modal,
  Radio,
  RadioGroup,
  useEscapeToClosePopup,
} from '@mangovoice/mango-ui'

import { useScheduleI18n } from '../../i18n'
import type {
  ScheduleEvent,
  ScheduleEventCreate,
  ScheduleEventId,
  ScheduleEventUpdate,
  ScheduleHoliday,
  ScheduleModalNotification,
} from '../../types'
import {
  dsText,
  fontSize,
  getMonthName,
  getOrdinalSuffix,
  getWeekdayName,
  ScheduleEventHelper,
  tokenVar,
} from '../../utils'
import {
  createScheduleModalNotifier,
  scheduleModalFieldGroupFocusStyles,
  scheduleModalInputStyles,
  scheduleModalInvalidInputStyles,
  scheduleModalLabelStyles,
  scheduleModalOptionLabelStyles,
} from '../shared'
import {
  buildOverrideDateTimePayload,
  buildOverrideSavePayload,
  getDefaultOverrideFormState,
  getDerivedStartDateValues,
  getOverrideValidationField,
  initializeOverrideFormState,
  type OverrideFormField,
  type OverrideFormState,
  type OverrideValidationCode,
  validateDateTimeOrder,
  validateOverrideFormState,
} from './overrideModalHelpers'

const normalizeHolidaySummary = (summary?: string | null): string =>
  summary?.trim().toLowerCase() ?? ''

const normalizeHolidayStartDate = (dtstart?: string | null): string =>
  dtstart?.trim().split('T')[0].replaceAll('-', '') ?? ''

const hasRecurrenceExceptions = (
  event?: Pick<ScheduleEvent | ScheduleHoliday, 'rdate' | 'exdate'> | null,
): boolean => !!(event?.rdate || event?.exdate)

const timeTextInputProps = {
  type: 'text',
  autoComplete: 'off',
} as const

const formatTimeOptionValue = (hour: number, minute: number): string => {
  const meridiem = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  const displayMinute = minute.toString().padStart(2, '0')
  return `${displayHour}:${displayMinute} ${meridiem}`
}

const formatTimeOptionText = (hour: number, minute: number): string => {
  const displayHour = hour % 12 || 12
  const displayMinute = minute.toString().padStart(2, '0')
  return `${displayHour}:${displayMinute}`
}

const TIME_OPTION_INTERVAL_MINUTES = 15
const HOURS_PER_MERIDIEM = 12
const TIME_OPTION_TEXT_VALUES = Array.from(
  { length: (HOURS_PER_MERIDIEM * 60) / TIME_OPTION_INTERVAL_MINUTES },
  (_, index) => {
    const totalMinutes = index * TIME_OPTION_INTERVAL_MINUTES
    const hour = Math.floor(totalMinutes / 60)
    const minute = totalMinutes % 60
    return formatTimeOptionText(hour, minute)
  },
)

type Meridiem = 'AM' | 'PM'

const MERIDIEM_OPTIONS: Meridiem[] = ['AM', 'PM']

const isMeridiem = (value: string): value is Meridiem =>
  value === 'AM' || value === 'PM'

const getTrailingMeridiem = (value: string): Meridiem | null => {
  const parsedMeridiem = value.slice(-2).toUpperCase()
  return isMeridiem(parsedMeridiem) ? parsedMeridiem : null
}

const trimTrailingWhitespace = (value: string): string => {
  let endIndex = value.length

  while (endIndex > 0 && value[endIndex - 1].trim() === '') {
    endIndex -= 1
  }

  return value.slice(0, endIndex)
}

const stripTrailingMeridiem = (value: string): string => {
  const meridiem = getTrailingMeridiem(value)
  if (!meridiem) return value

  return trimTrailingWhitespace(value.slice(0, -2))
}

const isDigitText = (
  value: string,
  minLength: number,
  maxLength: number,
): boolean =>
  value.length >= minLength &&
  value.length <= maxLength &&
  Array.from(value).every((character) => character >= '0' && character <= '9')

const normalizeTimeValueForDisplay = (timeValue: string): string | null => {
  const trimmedValue = timeValue.trim()
  const meridiem = getTrailingMeridiem(trimmedValue)
  const timeText = meridiem ? stripTrailingMeridiem(trimmedValue) : trimmedValue
  const colonIndex = timeText.indexOf(':')

  if (colonIndex <= 0 || colonIndex !== timeText.lastIndexOf(':')) return null

  const hourText = timeText.slice(0, colonIndex)
  const minuteText = timeText.slice(colonIndex + 1)

  if (!isDigitText(hourText, 1, 2) || !isDigitText(minuteText, 2, 2)) {
    return null
  }

  let hour = Number(hourText)
  const minute = Number(minuteText)

  if (minute < 0 || minute > 59) return null

  if (meridiem) {
    if (hour < 1 || hour > 12) return null
    hour = meridiem === 'PM' ? (hour % 12) + 12 : hour % 12
  } else if (hour < 0 || hour > 23) {
    return null
  }

  return formatTimeOptionValue(hour, minute)
}

const sanitizeTimeText = (timeText: string): string =>
  timeText.replace(/[^\d:]/g, '')

const splitTimeValue = (
  timeValue: string,
): { timeText: string; meridiem: Meridiem } => {
  const normalizedValue = normalizeTimeValueForDisplay(timeValue)
  const displayValue = normalizedValue ?? timeValue.trim()
  const parsedMeridiem = getTrailingMeridiem(displayValue)

  return {
    timeText: sanitizeTimeText(stripTrailingMeridiem(displayValue)),
    meridiem: parsedMeridiem ?? 'AM',
  }
}

const composeTimeValue = (timeText: string, meridiem: Meridiem): string => {
  const sanitizedTimeText = sanitizeTimeText(timeText)
  return `${sanitizedTimeText} ${meridiem}`.trim()
}

const normalizeTimeTextWithMeridiem = (
  timeText: string,
  meridiem: Meridiem,
): string | null =>
  normalizeTimeValueForDisplay(composeTimeValue(timeText, meridiem)) ??
  normalizeTimeValueForDisplay(timeText)

interface TimeTextInputHandle {
  /** Focuses the time text without opening the options list. */
  focus: () => void
}

interface TimeTextInputProps {
  value: string
  ariaLabel: string
  placeholder: string
  dataScheduleTimeInput: 'start' | 'end'
  invalid?: boolean
  describedBy?: string
  onChange: (value: string) => void
  ref?: React.Ref<TimeTextInputHandle>
}

const TimeTextInput: React.FC<TimeTextInputProps> = ({
  value,
  ariaLabel,
  placeholder,
  dataScheduleTimeInput,
  invalid = false,
  describedBy,
  onChange,
  ref,
}) => {
  const { text } = useScheduleI18n()
  const [isOpen, setIsOpen] = useState(false)
  const [isMeridiemOpen, setIsMeridiemOpen] = useState(false)
  const [activeOptionIndex, setActiveOptionIndex] = useState(0)
  const [activeMeridiemIndex, setActiveMeridiemIndex] = useState(0)
  const listboxId = useId()
  const meridiemListboxId = useId()
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const skipOpenOnFocusRef = React.useRef(false)

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        skipOpenOnFocusRef.current = true
        inputRef.current?.focus()
        skipOpenOnFocusRef.current = false
      },
    }),
    [],
  )
  const meridiemButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const isRestoringFocusRef = React.useRef(false)
  const { timeText, meridiem } = splitTimeValue(value)
  const options = TIME_OPTION_TEXT_VALUES
  const selectedOptionValue = timeText
  const selectedOptionIndex = options.indexOf(selectedOptionValue)

  const selectTimeOption = useCallback(
    (timeOption: string) => {
      onChange(composeTimeValue(timeOption, meridiem))
      setIsOpen(false)
      inputRef.current?.focus()
      inputRef.current?.select()
    },
    [meridiem, onChange],
  )

  const openOptions = useCallback(() => {
    setActiveOptionIndex(selectedOptionIndex)
    setIsMeridiemOpen(false)
    setIsOpen(true)
  }, [selectedOptionIndex])

  const handleTimeTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value
      const typedMeridiem = rawValue.match(/\b([AaPp][Mm])\b/)?.[1]
      const parsedMeridiem = typedMeridiem?.toUpperCase() ?? ''
      const nextMeridiem = isMeridiem(parsedMeridiem)
        ? parsedMeridiem
        : meridiem
      const nextTimeText = sanitizeTimeText(rawValue)
      // Pasted values like "10:30 PM" can include meridiem text in the time
      // segment; keep the meridiem and store only the numeric time text.
      onChange(composeTimeValue(nextTimeText, nextMeridiem))
      setActiveOptionIndex(options.indexOf(nextTimeText))
      setIsOpen(true)
    },
    [meridiem, onChange, options],
  )

  const selectMeridiem = useCallback(
    (nextMeridiem: Meridiem) => {
      onChange(composeTimeValue(timeText, nextMeridiem))
      setIsMeridiemOpen(false)
    },
    [onChange, timeText],
  )

  const openMeridiemOptions = useCallback(() => {
    setIsOpen(false)
    setActiveMeridiemIndex(Math.max(MERIDIEM_OPTIONS.indexOf(meridiem), 0))
    setIsMeridiemOpen(true)
  }, [meridiem])

  const toggleMeridiemOptions = useCallback(() => {
    if (isMeridiemOpen) {
      setIsMeridiemOpen(false)
    } else {
      openMeridiemOptions()
    }
  }, [isMeridiemOpen, openMeridiemOptions])

  // Set when keydown handles Enter/Space, so the click the browser derives
  // from that key (Firefox fires it on Space keyup) can't toggle again.
  const handledActivationKeyRef = React.useRef(false)

  // Select-only combobox keys: focus stays on the trigger while
  // aria-activedescendant tracks the highlighted option.
  const handleMeridiemKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handledActivationKeyRef.current = true
      }
      const lastIndex = MERIDIEM_OPTIONS.length - 1
      const typedMeridiem = MERIDIEM_OPTIONS.find(
        (option) => option[0] === event.key.toUpperCase(),
      )
      if (typedMeridiem && !event.altKey && !event.ctrlKey && !event.metaKey) {
        event.preventDefault()
        selectMeridiem(typedMeridiem)
        return
      }

      if (!isMeridiemOpen) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault()
          openMeridiemOptions()
        }
        return
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setActiveMeridiemIndex((index) => Math.min(index + 1, lastIndex))
          break
        case 'ArrowUp':
          event.preventDefault()
          setActiveMeridiemIndex((index) => Math.max(index - 1, 0))
          break
        case 'Home':
          event.preventDefault()
          setActiveMeridiemIndex(0)
          break
        case 'End':
          event.preventDefault()
          setActiveMeridiemIndex(lastIndex)
          break
        case 'Enter':
        case ' ':
          event.preventDefault()
          selectMeridiem(MERIDIEM_OPTIONS[activeMeridiemIndex] ?? meridiem)
          break
        case 'Tab':
          setIsMeridiemOpen(false)
          break
      }
    },
    [
      activeMeridiemIndex,
      isMeridiemOpen,
      meridiem,
      openMeridiemOptions,
      selectMeridiem,
    ],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        setIsOpen(true)
        setActiveOptionIndex((currentIndex) => {
          const direction = event.key === 'ArrowDown' ? 1 : -1
          const startIndex = isOpen
            ? currentIndex
            : selectedOptionIndex >= 0
              ? selectedOptionIndex
              : direction === 1
                ? -1
                : 0
          return (startIndex + direction + options.length) % options.length
        })
        return
      }

      if (event.key === 'Enter' && isOpen) {
        event.preventDefault()
        const activeOption = options[activeOptionIndex]
        if (activeOption) {
          selectTimeOption(activeOption)
        }
        return
      }
    },
    [
      activeOptionIndex,
      isOpen,
      options,
      selectedOptionIndex,
      selectTimeOption,
      timeText,
    ],
  )

  useEscapeToClosePopup(isOpen || isMeridiemOpen, () => {
    const trigger = isOpen ? inputRef.current : meridiemButtonRef.current
    setIsOpen(false)
    setIsMeridiemOpen(false)
    isRestoringFocusRef.current = true
    trigger?.focus()
    isRestoringFocusRef.current = false
  })

  useEffect(() => {
    if (!isOpen) return
    if (activeOptionIndex < 0) return

    const activeOption = document.getElementById(
      `${listboxId}-option-${activeOptionIndex}`,
    )
    activeOption?.scrollIntoView?.({ block: 'nearest' })
  }, [activeOptionIndex, isOpen, listboxId])

  useEffect(() => {
    if (!isOpen && !isMeridiemOpen) return

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
        setIsMeridiemOpen(false)
      }
    }

    document.addEventListener('mousedown', handleDocumentMouseDown)
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown)
    }
  }, [isOpen, isMeridiemOpen])

  return (
    <Box ref={rootRef} position="relative" w="152px" flexShrink={0}>
      <Box
        display="flex"
        alignItems="stretch"
        minH="44px"
        overflow="hidden"
        borderWidth="1px"
        borderColor={
          invalid ? tokenVar('fg.error') : tokenVar('border.default')
        }
        borderRadius="8px"
        bg={tokenVar('bg.muted')}
        color={tokenVar('fg.default')}
        {...scheduleModalFieldGroupFocusStyles}
      >
        <Input
          {...timeTextInputProps}
          ref={inputRef}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          aria-controls={isOpen ? listboxId : undefined}
          aria-expanded={isOpen}
          aria-activedescendant={
            isOpen && activeOptionIndex >= 0
              ? `${listboxId}-option-${activeOptionIndex}`
              : undefined
          }
          role="combobox"
          data-schedule-time-input={dataScheduleTimeInput}
          placeholder={placeholder}
          value={timeText}
          onFocus={() => {
            if (!skipOpenOnFocusRef.current && !isRestoringFocusRef.current) {
              openOptions()
            }
          }}
          onClick={openOptions}
          onBlur={(e) => {
            const normalizedValue = normalizeTimeTextWithMeridiem(
              e.currentTarget.value,
              meridiem,
            )
            if (normalizedValue && normalizedValue !== value) {
              onChange(normalizedValue)
            }
            setIsOpen(false)
          }}
          onChange={handleTimeTextChange}
          onKeyDown={handleKeyDown}
          px={2}
          flex={1}
          minW="88px"
          minH="42px"
          borderWidth={0}
          borderRadius={0}
          bg="transparent"
          color={tokenVar('fg.default')}
          fontFamily={dsText.Body2.fontFamily}
          fontSize={fontSize(dsText.Body2.fontSize)}
          fontWeight={dsText.Body2.fontWeight}
          lineHeight={dsText.Body2.lineHeight}
          _placeholder={{
            color: tokenVar('fg.subtle'),
          }}
          focusVisibleRing="none"
        />
        <Box
          w="1px"
          flexShrink={0}
          alignSelf="stretch"
          bg={tokenVar('border.default')}
        />
        <Box
          as="button"
          ref={meridiemButtonRef}
          role="combobox"
          aria-label={text(
            'schedule.modal.override.meridiemAriaLabel',
            '{{label}} AM/PM',
            { label: ariaLabel },
          )}
          aria-haspopup="listbox"
          aria-expanded={isMeridiemOpen}
          aria-controls={isMeridiemOpen ? meridiemListboxId : undefined}
          aria-activedescendant={
            isMeridiemOpen
              ? `${meridiemListboxId}-option-${activeMeridiemIndex}`
              : undefined
          }
          w="54px"
          flexShrink={0}
          minH="42px"
          px={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="transparent"
          color={tokenVar('fg.default')}
          fontFamily={dsText.Body2.fontFamily}
          fontSize={fontSize(dsText.Body2.fontSize)}
          fontWeight={dsText.Body2.fontWeight}
          lineHeight={dsText.Body2.lineHeight}
          cursor="pointer"
          _hover={{ bg: tokenVar('gray.subtle') }}
          _focusVisible={{
            boxShadow: 'none',
            bg: tokenVar('brand.subtle'),
          }}
          onClick={(event: React.MouseEvent) => {
            if (event.detail === 0 && handledActivationKeyRef.current) {
              handledActivationKeyRef.current = false
              return
            }
            toggleMeridiemOptions()
          }}
          onKeyDown={handleMeridiemKeyDown}
          onKeyUp={(event: React.KeyboardEvent) => {
            if (event.key === ' ') event.preventDefault()
            if (event.key === 'Enter' || event.key === ' ') {
              // Any click derived from this key fires before this timeout.
              setTimeout(() => {
                handledActivationKeyRef.current = false
              })
            }
          }}
          onBlur={() => setIsMeridiemOpen(false)}
        >
          {meridiem}
        </Box>
      </Box>
      {isMeridiemOpen && (
        <Box
          id={meridiemListboxId}
          role="listbox"
          aria-label={text(
            'schedule.modal.override.meridiemOptionsAriaLabel',
            '{{label}} AM/PM options',
            { label: ariaLabel },
          )}
          position="absolute"
          top="calc(100% + 4px)"
          right={0}
          zIndex="popover"
          w="58px"
          overflow="hidden"
          bg={tokenVar('bg.default')}
          borderWidth="1px"
          borderColor={tokenVar('border.default')}
          borderRadius="8px"
          boxShadow="md"
        >
          {MERIDIEM_OPTIONS.map((meridiemOption, index) => {
            const isSelected = meridiemOption === meridiem
            const isActive = index === activeMeridiemIndex
            return (
              <Box
                as="button"
                id={`${meridiemListboxId}-option-${index}`}
                key={meridiemOption}
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                w="full"
                px={3}
                py={2}
                textAlign="center"
                cursor="pointer"
                bg={
                  isActive || isSelected
                    ? tokenVar('blue.subtle')
                    : tokenVar('bg.default')
                }
                color={tokenVar('fg.default')}
                fontFamily={dsText.Body2.fontFamily}
                fontSize={fontSize(dsText.Body2.fontSize)}
                fontWeight={dsText.Body2.fontWeight}
                lineHeight={dsText.Body2.lineHeight}
                _hover={{ bg: tokenVar('gray.subtle') }}
                onMouseEnter={() => setActiveMeridiemIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  selectMeridiem(meridiemOption)
                }}
              >
                {meridiemOption}
              </Box>
            )
          })}
        </Box>
      )}
      {isOpen && (
        <Box
          id={listboxId}
          role="listbox"
          aria-label={text(
            'schedule.modal.override.timeOptionsAriaLabel',
            '{{label}} options',
            { label: ariaLabel },
          )}
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex="popover"
          maxH="180px"
          overflowY="auto"
          bg={tokenVar('bg.default')}
          borderWidth="1px"
          borderColor={tokenVar('border.default')}
          borderRadius="8px"
          boxShadow="md"
        >
          {options.map((timeOption, index) => {
            const isSelected = timeOption === selectedOptionValue
            const isActive = index === activeOptionIndex
            return (
              <Box
                as="button"
                id={`${listboxId}-option-${index}`}
                key={timeOption}
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                w="full"
                px={3}
                py={2}
                textAlign="left"
                cursor="pointer"
                bg={
                  isActive || isSelected
                    ? tokenVar('blue.subtle')
                    : tokenVar('bg.default')
                }
                color={tokenVar('fg.default')}
                fontFamily={dsText.Body2.fontFamily}
                fontSize={fontSize(dsText.Body2.fontSize)}
                fontWeight={dsText.Body2.fontWeight}
                lineHeight={dsText.Body2.lineHeight}
                _hover={{ bg: tokenVar('gray.subtle') }}
                onMouseEnter={() => setActiveOptionIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  selectTimeOption(timeOption)
                }}
              >
                {timeOption}
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export interface EditOverrideModalProps {
  isOpen: boolean
  onClose: () => void
  mode: 'create' | 'edit'
  event: ScheduleEvent | null // null when mode='create'
  eventHelper: ScheduleEventHelper | null // null when mode='create'
  scheduleTimeZone: string
  holidayTemplates?: ScheduleHoliday[]
  areHolidayTemplatesLoading?: boolean
  holidayTemplatesError?: unknown
  onSave: (eventId: ScheduleEventId, data: ScheduleEventUpdate) => void
  onCreate?: (data: ScheduleEventCreate) => void
  onNotify?: (notification: ScheduleModalNotification) => void
}

export const EditOverrideModal: React.FC<EditOverrideModalProps> = ({
  isOpen,
  onClose,
  mode,
  event,
  eventHelper,
  scheduleTimeZone,
  holidayTemplates = [],
  areHolidayTemplatesLoading = false,
  holidayTemplatesError,
  onSave,
  onCreate,
  onNotify,
}) => {
  const { text } = useScheduleI18n()
  const toaster = useMemo(
    () => createScheduleModalNotifier(onNotify),
    [onNotify],
  )

  const isCreateMode = mode === 'create'

  // Initialize form state from event (or default for create mode)
  const [formState, setFormState] = useState<OverrideFormState>(() => {
    if (isCreateMode || !event || !eventHelper) {
      return getDefaultOverrideFormState()
    }
    return initializeOverrideFormState(event, eventHelper)
  })
  const startDateInputRef = React.useRef<HTMLInputElement | null>(null)
  const endDateInputRef = React.useRef<HTMLInputElement | null>(null)
  const nameInputRef = React.useRef<HTMLInputElement | null>(null)
  const startTimeInputRef = React.useRef<TimeTextInputHandle | null>(null)
  const endTimeInputRef = React.useRef<TimeTextInputHandle | null>(null)
  const repeatFrequencyRef = React.useRef<HTMLSelectElement | null>(null)
  const firstWeekdayInputRef = React.useRef<HTMLInputElement | null>(null)
  const repeatCountInputRef = React.useRef<HTMLInputElement | null>(null)
  const repeatUntilInputRef = React.useRef<HTMLInputElement | null>(null)
  const fieldErrorId = useId()
  const [fieldErrorState, setFieldErrorState] = useState<{
    field: OverrideFormField
    message: string
    formValues: string
  } | null>(null)
  // The error is cleared as soon as the user changes a form value.
  const fieldError =
    fieldErrorState?.formValues === JSON.stringify(formState)
      ? fieldErrorState
      : null

  useEffect(() => {
    if (!isOpen) setFieldErrorState(null)
  }, [isOpen])

  useEffect(() => {
    if (!fieldErrorState) return
    const fieldRefs: Record<
      OverrideFormField,
      React.RefObject<{ focus: () => void } | null>
    > = {
      name: nameInputRef,
      startDate: startDateInputRef,
      endDate: endDateInputRef,
      startTime: startTimeInputRef,
      endTime: endTimeInputRef,
      repeatFrequency: repeatFrequencyRef,
      repeatWeekdays: firstWeekdayInputRef,
      repeatCount: repeatCountInputRef,
      repeatUntil: repeatUntilInputRef,
    }
    fieldRefs[fieldErrorState.field].current?.focus()
  }, [fieldErrorState])

  const getFieldErrorProps = (field: OverrideFormField) =>
    fieldError?.field === field
      ? { 'aria-invalid': true, 'aria-describedby': fieldErrorId }
      : {}

  const renderFieldError = (fields: OverrideFormField[]) =>
    fieldError && fields.includes(fieldError.field) ? (
      <Text
        id={fieldErrorId}
        mt={2}
        color={tokenVar('fg.error')}
        fontFamily={dsText.Body3.fontFamily}
        fontSize={fontSize(dsText.Body3.fontSize)}
        fontWeight={dsText.Body3.fontWeight}
        lineHeight={dsText.Body3.lineHeight}
      >
        {fieldError.message}
      </Text>
    ) : null
  const isFormReady = isCreateMode || Boolean(event && eventHelper)
  const userInteractedWhileLoadingRef = React.useRef(false)

  // Edit mode opens on "Loading override details…", so the dialog's initial
  // focus lands on Cancel. Track whether the user moves on while it loads.
  useEffect(() => {
    if (!isOpen || isFormReady) return
    userInteractedWhileLoadingRef.current = false
    const markInteraction = () => {
      userInteractedWhileLoadingRef.current = true
    }
    document.addEventListener('keydown', markInteraction, true)
    document.addEventListener('pointerdown', markInteraction, true)
    return () => {
      document.removeEventListener('keydown', markInteraction, true)
      document.removeEventListener('pointerdown', markInteraction, true)
    }
  }, [isOpen, isFormReady])

  // Once the details load, move focus to Name unless the user already moved it.
  const wasFormReadyRef = React.useRef(isFormReady)
  useEffect(() => {
    const wasFormReady = wasFormReadyRef.current
    wasFormReadyRef.current = isFormReady
    if (!isOpen || !isFormReady || wasFormReady) return
    if (userInteractedWhileLoadingRef.current) return
    nameInputRef.current?.focus()
  }, [isOpen, isFormReady])

  // Reset form when event changes or mode changes
  useEffect(() => {
    if (isCreateMode) {
      setFormState(getDefaultOverrideFormState())
    } else if (event && eventHelper) {
      setFormState(initializeOverrideFormState(event, eventHelper))
    } else {
      setFormState(getDefaultOverrideFormState())
    }
  }, [isCreateMode, event, eventHelper])

  const normalizedEventName = normalizeHolidaySummary(event?.summary)
  const normalizedEventStartDate = normalizeHolidayStartDate(event?.dtstart)
  const matchingHolidayTemplate = useMemo(() => {
    if (event?.switchboard_routing !== 'HOLIDAY') {
      return undefined
    }

    const summaryMatch = holidayTemplates.find(
      (holiday) =>
        normalizedEventName &&
        normalizeHolidaySummary(holiday.summary) === normalizedEventName,
    )
    if (summaryMatch) return summaryMatch

    // Renamed saved holidays can lose the summary match; the date fallback is
    // intentionally limited to irregular templates to reduce collision risk.
    return holidayTemplates.find(
      (holiday) =>
        hasRecurrenceExceptions(holiday) &&
        normalizedEventStartDate &&
        normalizeHolidayStartDate(holiday.dtstart) === normalizedEventStartDate,
    )
  }, [
    event?.switchboard_routing,
    holidayTemplates,
    normalizedEventName,
    normalizedEventStartDate,
  ])
  const lockedHolidayTemplate = hasRecurrenceExceptions(matchingHolidayTemplate)
    ? matchingHolidayTemplate
    : undefined
  const needsHolidayTemplateRecurrenceCheck =
    !isCreateMode &&
    event?.switchboard_routing === 'HOLIDAY' &&
    !hasRecurrenceExceptions(event) &&
    (!!normalizedEventName || !!normalizedEventStartDate)
  const isHolidayTemplateCheckPending =
    needsHolidayTemplateRecurrenceCheck &&
    areHolidayTemplatesLoading &&
    !matchingHolidayTemplate
  const isHolidayTemplateCheckUnavailable =
    needsHolidayTemplateRecurrenceCheck &&
    !areHolidayTemplatesLoading &&
    !!holidayTemplatesError &&
    !matchingHolidayTemplate

  // Check for locked holiday repeat patterns - only relevant in edit mode. The
  // event's own rdate/exdate is preferred; holiday templates cover older saved
  // events that were created without those recurrence exception fields.
  const hasIrregularRepeat =
    !isCreateMode && (hasRecurrenceExceptions(event) || !!lockedHolidayTemplate)
  const lockDateRepeatControls =
    hasIrregularRepeat ||
    isHolidayTemplateCheckPending ||
    isHolidayTemplateCheckUnavailable
  const irregularRepeatEvent = useMemo<ScheduleEvent | null>(() => {
    if (!event || !lockedHolidayTemplate) return event

    return {
      ...event,
      dtstart: event.dtstart || lockedHolidayTemplate.dtstart,
      dtend: event.dtend ?? lockedHolidayTemplate.dtend,
      duration: event.duration ?? lockedHolidayTemplate.duration,
      rrule: event.rrule ?? lockedHolidayTemplate.rrule ?? null,
      rdate: event.rdate ?? lockedHolidayTemplate.rdate ?? null,
      exdate: event.exdate ?? lockedHolidayTemplate.exdate ?? null,
    }
  }, [event, lockedHolidayTemplate])
  const irregularRepeatHelper = useMemo(() => {
    if (!hasIrregularRepeat || !irregularRepeatEvent) return eventHelper

    try {
      return new ScheduleEventHelper(irregularRepeatEvent, scheduleTimeZone)
    } catch {
      return eventHelper
    }
  }, [eventHelper, hasIrregularRepeat, irregularRepeatEvent, scheduleTimeZone])
  const lockedDateInputProps = lockDateRepeatControls
    ? ({
        disabled: true,
        readOnly: true,
        'aria-disabled': true,
        tabIndex: -1,
        pointerEvents: 'none',
        cursor: 'not-allowed',
      } as const)
    : {}
  const irregularRepeatName =
    formState.name ||
    event?.summary ||
    text('schedule.common.holiday', 'Holiday')
  const irregularRepeatNextOccurrence =
    irregularRepeatHelper?.nextOccurrenceToText()
  const holidayTemplateLockMessage = isHolidayTemplateCheckPending
    ? text(
        'schedule.modal.override.holidayTemplateCheckPending',
        'Checking {{name}} repeat pattern. Date and repeat controls are temporarily unavailable.',
        { name: irregularRepeatName },
      )
    : text(
        'schedule.modal.override.holidayTemplateCheckUnavailable',
        '{{name}} repeat pattern could not be verified. Date and repeat controls are unavailable.',
        { name: irregularRepeatName },
      )

  // Handle form field updates
  const updateField = useCallback(
    <K extends keyof OverrideFormState>(
      field: K,
      value: OverrideFormState[K],
    ) => {
      setFormState((prev) => ({ ...prev, [field]: value }))
    },
    [],
  )

  const updateRepeatField = useCallback(
    <K extends keyof OverrideFormState['repeat']>(
      field: K,
      value: OverrideFormState['repeat'][K],
    ) => {
      setFormState((prev) => ({
        ...prev,
        repeat: { ...prev.repeat, [field]: value },
      }))
    },
    [],
  )

  // Handle date type change
  const handleDateTypeChange = useCallback(
    (details: { value: string | null }) => {
      if (lockDateRepeatControls) return
      if (!details.value) return
      const value = details.value as 'SINGLE_DAY' | 'MULTIPLE_DAYS'
      updateField('dateType', value)
      if (value === 'SINGLE_DAY') {
        updateField('endDate', undefined)
      } else if (value === 'MULTIPLE_DAYS' && !formState.endDate) {
        // Set end date to next day to ensure it's truly multi-day
        const startDate = new Date(formState.startDate)
        startDate.setDate(startDate.getDate() + 1)
        const nextDay = startDate.toISOString().split('T')[0]
        updateField('endDate', nextDay)
      }
    },
    [
      formState.startDate,
      formState.endDate,
      lockDateRepeatControls,
      updateField,
    ],
  )

  // Handle all day toggle
  const handleAllDayChange = useCallback(
    (details: { value: string | null }) => {
      if (!details.value) return
      const isAllDay = details.value === 'allDay'
      updateField('isAllDay', isAllDay)
      if (isAllDay) {
        updateField('startTime', undefined)
        updateField('endTime', undefined)
      } else {
        // Set default times if not set
        if (!formState.startTime) {
          updateField('startTime', '12:00 AM')
        }
        if (!formState.endTime) {
          updateField('endTime', '11:59 PM')
        }
      }
    },
    [formState.startTime, formState.endTime, updateField],
  )

  // Handle weekday toggle for weekly repeat
  const handleWeekdayToggle = useCallback(
    (weekday: number, checked: boolean) => {
      const currentWeekdays = formState.repeat.byweekday || []
      let newWeekdays: number[]
      if (checked) {
        newWeekdays = [...currentWeekdays, weekday].sort((a, b) => a - b)
      } else {
        newWeekdays = currentWeekdays.filter((d) => d !== weekday)
      }
      updateRepeatField('byweekday', newWeekdays)
    },
    [formState.repeat.byweekday, updateRepeatField],
  )

  // Handle frequency change with state initialization
  const handleFrequencyChange = useCallback(
    (newFrequency: OverrideFormState['repeat']['frequency']) => {
      const derived = getDerivedStartDateValues(formState.startDate)

      setFormState((prev) => {
        const newRepeat = { ...prev.repeat, frequency: newFrequency }

        // Initialize state based on new frequency
        if (newFrequency === 'WEEKLY') {
          // Initialize byweekday to include start date's weekday if not set
          if (!newRepeat.byweekday || newRepeat.byweekday.length === 0) {
            newRepeat.byweekday = [derived.weekday]
          }
        } else if (newFrequency === 'MONTHLY') {
          // Initialize monthlyType and derived values
          if (!newRepeat.monthlyType) {
            newRepeat.monthlyType = 'SPECIFIC_DATE'
          }
          newRepeat.day = derived.day
          newRepeat.weekday = derived.weekday
          newRepeat.setpos = derived.setpos
        } else if (newFrequency === 'YEARLY') {
          // Initialize yearlyType and derived values from start date
          if (!newRepeat.yearlyType) {
            newRepeat.yearlyType = 'SPECIFIC_DATE'
          }
          newRepeat.month = derived.month
          newRepeat.day = derived.day
          newRepeat.weekday = derived.weekday
          newRepeat.setpos = derived.setpos
        }

        return { ...prev, repeat: newRepeat }
      })
    },
    [formState.startDate],
  )

  // Handle start date change - update derived values for monthly/yearly patterns
  const handleStartDateChange = useCallback((newStartDate: string) => {
    const derived = getDerivedStartDateValues(newStartDate)

    setFormState((prev) => {
      const newRepeat = { ...prev.repeat }

      // Update derived values for monthly patterns
      if (newRepeat.frequency === 'MONTHLY') {
        newRepeat.day = derived.day
        newRepeat.weekday = derived.weekday
        newRepeat.setpos = derived.setpos
      }

      // Update derived values for yearly patterns
      if (newRepeat.frequency === 'YEARLY') {
        newRepeat.month = derived.month
        newRepeat.day = derived.day
        newRepeat.weekday = derived.weekday
        newRepeat.setpos = derived.setpos
      }

      return { ...prev, startDate: newStartDate, repeat: newRepeat }
    })
  }, [])

  const handlePickerInputClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      const input = event.currentTarget as HTMLInputElement & {
        showPicker?: () => void
      }

      if (typeof input.showPicker !== 'function') return

      try {
        input.showPicker()
      } catch {
        // Ignore unsupported/browser restriction errors and keep native behavior.
      }
    },
    [],
  )

  const getValidationMessage = useCallback(
    (code: OverrideValidationCode): string => {
      switch (code) {
        case 'NAME_REQUIRED':
          return text(
            'schedule.modal.override.validation.nameRequired',
            'Please enter a name for the override',
          )
        case 'MULTI_DAY_END_DATE_REQUIRED':
          return text(
            'schedule.modal.override.validation.endDateRequired',
            'Please select an end date for multi-day events',
          )
        case 'START_END_TIME_REQUIRED':
          return text(
            'schedule.modal.override.validation.startEndTimeRequired',
            'Please select start and end times',
          )
        case 'END_MUST_BE_AFTER_START':
          return text(
            'schedule.modal.override.validation.endAfterStart',
            'End date/time must be after start date/time',
          )
        case 'WEEKLY_WEEKDAY_REQUIRED':
          return text(
            'schedule.modal.override.validation.weekdayRequired',
            'Please select at least one day of the week',
          )
        case 'MONTHLY_PATTERN_REQUIRED':
          return text(
            'schedule.modal.override.validation.monthlyPatternRequired',
            'Please select a monthly repeat pattern',
          )
        case 'VALID_DAY_REQUIRED':
          return text(
            'schedule.modal.override.validation.validDayRequired',
            'Please select a valid day (1-31)',
          )
        case 'VALID_WEEKDAY_PATTERN_REQUIRED':
          return text(
            'schedule.modal.override.validation.validWeekdayPatternRequired',
            'Please select a valid weekday pattern',
          )
        case 'YEARLY_MONTH_REQUIRED':
          return text(
            'schedule.modal.override.validation.monthRequired',
            'Please select a month for yearly repeat',
          )
        case 'REPEAT_COUNT_REQUIRED':
          return text(
            'schedule.modal.override.validation.occurrencesRequired',
            'Please enter the number of occurrences',
          )
        case 'REPEAT_UNTIL_REQUIRED':
          return text(
            'schedule.modal.override.validation.endDateRequiredShort',
            'Please select an end date',
          )
      }
    },
    [text],
  )

  // Handle save (for both create and edit modes)
  const handleSave = useCallback(() => {
    if (!isCreateMode && !event) return

    let saveFormState: OverrideFormState = {
      ...formState,
      startDate: startDateInputRef.current?.value || formState.startDate,
      endDate:
        formState.dateType === 'MULTIPLE_DAYS'
          ? endDateInputRef.current?.value || formState.endDate
          : undefined,
    }
    if (lockDateRepeatControls && event && eventHelper) {
      const originalFormState = initializeOverrideFormState(event, eventHelper)
      saveFormState = {
        ...formState,
        dateType: originalFormState.dateType,
        startDate: originalFormState.startDate,
        endDate: originalFormState.endDate,
        repeat: originalFormState.repeat,
      }
    }

    const showFieldError = (field: OverrideFormField, message: string) => {
      setFieldErrorState({
        field,
        message,
        formValues: JSON.stringify(formState),
      })
      toaster.create({
        title: text('schedule.common.validationErrorTitle', 'Validation Error'),
        description: message,
        type: 'error',
        duration: 3000,
        closable: true,
      })
    }

    const validationError = validateOverrideFormState(saveFormState)
    if (validationError) {
      showFieldError(
        getOverrideValidationField(validationError, saveFormState),
        getValidationMessage(validationError),
      )
      return
    }

    let startZdt
    let endZdt
    try {
      const dateTimePayload = buildOverrideDateTimePayload(
        saveFormState,
        scheduleTimeZone,
      )
      startZdt = dateTimePayload.startZdt
      endZdt = dateTimePayload.endZdt
    } catch (error) {
      showFieldError(
        saveFormState.isAllDay ? 'startDate' : 'startTime',
        error instanceof Error
          ? error.message
          : text(
              'schedule.modal.override.validation.invalidDateTime',
              'Please enter valid date and time values',
            ),
      )
      return
    }

    const dateTimeOrderError = validateDateTimeOrder(startZdt, endZdt)
    if (dateTimeOrderError) {
      showFieldError(
        getOverrideValidationField(dateTimeOrderError, saveFormState),
        getValidationMessage(dateTimeOrderError),
      )
      return
    }

    const payload = buildOverrideSavePayload(
      saveFormState,
      startZdt,
      endZdt,
      scheduleTimeZone,
    )

    if (isCreateMode && onCreate) {
      onCreate(payload as ScheduleEventCreate)
    } else if (!isCreateMode && event) {
      const editPayload: ScheduleEventUpdate = hasIrregularRepeat
        ? {
            ...payload,
            rrule: event.rrule ?? lockedHolidayTemplate?.rrule ?? null,
            rdate: event.rdate ?? lockedHolidayTemplate?.rdate ?? null,
            exdate: event.exdate ?? lockedHolidayTemplate?.exdate ?? null,
          }
        : saveFormState.repeat.frequency === 'NONE'
          ? { ...payload, rrule: null }
          : payload
      onSave(event.id, editPayload)
    }

    onClose()
  }, [
    event,
    eventHelper,
    formState,
    getValidationMessage,
    hasIrregularRepeat,
    isCreateMode,
    lockDateRepeatControls,
    lockedHolidayTemplate,
    onClose,
    onCreate,
    onSave,
    scheduleTimeZone,
    text,
    toaster,
  ])

  // Determine if we can show the form

  const sectionHeadingStyles = {
    fontFamily: dsText.Body1_Bold.fontFamily,
    fontSize: fontSize(dsText.Body1_Bold.fontSize),
    fontWeight: dsText.Body1_Bold.fontWeight,
    lineHeight: dsText.Body1_Bold.lineHeight,
    color: tokenVar('fg.default'),
  }

  const supportingTextStyles = {
    fontFamily: dsText.Body2.fontFamily,
    fontSize: fontSize(dsText.Body2.fontSize),
    fontWeight: dsText.Body2.fontWeight,
    lineHeight: dsText.Body2.lineHeight,
    color: tokenVar('fg.muted'),
  }

  const radioLabelStyles = {
    ...scheduleModalOptionLabelStyles,
    paddingLeft: '4px',
  }
  const dateTimeOptionColumnWidth = '180px'
  const dateTimeOptionGroupWidth = '376px'

  return (
    <Modal
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
      title={
        isCreateMode
          ? text('schedule.modal.override.titleCreate', 'Add Override')
          : text('schedule.modal.override.titleEdit', 'Edit Schedule Override')
      }
      maxWidth="700px"
      footer={
        <HStack gap={3} w="full">
          <Button
            variant="outline"
            onClick={onClose}
            flex={1}
            h="44px"
            borderColor={tokenVar('border.default')}
            bg={tokenVar('bg.default')}
            color={tokenVar('fg.default')}
            borderRadius="4px"
            fontFamily={dsText.Body1_Bold.fontFamily}
            fontSize={fontSize(dsText.Body1_Bold.fontSize)}
            fontWeight={dsText.Body1_Bold.fontWeight}
            lineHeight={dsText.Body1_Bold.lineHeight}
            _hover={{ bg: tokenVar('gray.subtle') }}
          >
            {text('schedule.common.cancel', 'Cancel')}
          </Button>
          <Button
            colorPalette="brand"
            onClick={handleSave}
            disabled={!isFormReady}
            flex={1}
            h="44px"
            borderRadius="4px"
            fontFamily={dsText.Body1_Bold.fontFamily}
            fontSize={fontSize(dsText.Body1_Bold.fontSize)}
            fontWeight={dsText.Body1_Bold.fontWeight}
            lineHeight={dsText.Body1_Bold.lineHeight}
          >
            {isCreateMode
              ? text('schedule.common.create', 'Create')
              : text('schedule.common.save', 'Save')}
          </Button>
        </HStack>
      }
    >
      {!isFormReady ? (
        <Box textAlign="center" py={8}>
          <Text color="fg.muted">
            {text(
              'schedule.modal.override.loadingDetails',
              'Loading override details...',
            )}
          </Text>
        </Box>
      ) : (
        <VStack gap={6} align="stretch">
          {/* Name */}
          <Field.Root
            required
            invalid={fieldError?.field === 'name'}
            ids={{ errorText: fieldErrorId }}
          >
            <Field.Label style={scheduleModalLabelStyles}>
              {text('schedule.common.name', 'Name')}
            </Field.Label>
            <Input
              ref={nameInputRef}
              {...getFieldErrorProps('name')}
              value={formState.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder={text(
                'schedule.modal.override.namePlaceholder',
                'Override name',
              )}
              px={3}
              {...scheduleModalInputStyles}
              {...scheduleModalInvalidInputStyles}
            />
            {fieldError?.field === 'name' && (
              <Field.ErrorText>{fieldError.message}</Field.ErrorText>
            )}
          </Field.Root>

          {/* Date & Time */}
          <Box>
            <HStack justify="space-between" align="center" mb={3}>
              <Text style={sectionHeadingStyles}>
                {text('schedule.modal.override.dateTime', 'Date & Time')}
              </Text>
              {/* Single Day vs Multiple Days */}
              <RadioGroup
                value={formState.dateType}
                onValueChange={handleDateTypeChange}
                w={dateTimeOptionGroupWidth}
                disabled={lockDateRepeatControls}
              >
                <HStack gap={4} justify="space-between" w="full">
                  <Radio
                    value="SINGLE_DAY"
                    w={dateTimeOptionColumnWidth}
                    display="flex"
                    alignItems="center"
                  >
                    <Text as="span" style={radioLabelStyles}>
                      {text(
                        'schedule.modal.override.dateType.singleDay',
                        'Single Day',
                      )}
                    </Text>
                  </Radio>
                  <Radio
                    value="MULTIPLE_DAYS"
                    w={dateTimeOptionColumnWidth}
                    display="flex"
                    alignItems="center"
                  >
                    <Text as="span" style={radioLabelStyles}>
                      {text(
                        'schedule.modal.override.dateType.multipleDays',
                        'Multiple Days',
                      )}
                    </Text>
                  </Radio>
                </HStack>
              </RadioGroup>
            </HStack>
            <VStack gap={4} align="stretch">
              {/* All Day vs Set Hours */}
              <HStack justify="flex-end">
                <RadioGroup
                  value={formState.isAllDay ? 'allDay' : 'hours'}
                  onValueChange={handleAllDayChange}
                  w={dateTimeOptionGroupWidth}
                >
                  <HStack gap={4} justify="space-between" w="full">
                    <Radio
                      value="allDay"
                      w={dateTimeOptionColumnWidth}
                      display="flex"
                      alignItems="center"
                    >
                      <Text as="span" style={radioLabelStyles}>
                        {text(
                          'schedule.modal.override.timeType.closedAllDay',
                          'Closed All Day',
                        )}
                      </Text>
                    </Radio>
                    <Radio
                      value="hours"
                      w={dateTimeOptionColumnWidth}
                      display="flex"
                      alignItems="center"
                    >
                      <Text as="span" style={radioLabelStyles}>
                        {text(
                          'schedule.modal.override.timeType.setClosedHours',
                          'Set Closed Hours',
                        )}
                      </Text>
                    </Radio>
                  </HStack>
                </RadioGroup>
              </HStack>

              {/* Date/Time inputs - layout depends on isAllDay */}
              {formState.isAllDay ? (
                /* All Day: show date inputs only */
                <HStack gap={2} align="center">
                  <Text style={scheduleModalLabelStyles} flexShrink={0}>
                    {text('schedule.common.closed', 'Closed')}
                  </Text>
                  <Input
                    ref={startDateInputRef}
                    type="date"
                    value={formState.startDate}
                    onClick={
                      lockDateRepeatControls
                        ? undefined
                        : handlePickerInputClick
                    }
                    onChange={
                      lockDateRepeatControls
                        ? undefined
                        : (e) => handleStartDateChange(e.target.value)
                    }
                    aria-label={text(
                      'schedule.modal.override.startDateAriaLabel',
                      'Start date',
                    )}
                    px={2}
                    w="140px"
                    flexShrink={0}
                    {...scheduleModalInputStyles}
                    {...scheduleModalInvalidInputStyles}
                    {...getFieldErrorProps('startDate')}
                    {...lockedDateInputProps}
                  />
                  {formState.dateType === 'MULTIPLE_DAYS' && (
                    <>
                      <Text style={supportingTextStyles} flexShrink={0}>
                        {text('schedule.common.to', 'to')}
                      </Text>
                      <Input
                        ref={endDateInputRef}
                        type="date"
                        value={formState.endDate || formState.startDate}
                        onClick={
                          lockDateRepeatControls
                            ? undefined
                            : handlePickerInputClick
                        }
                        onChange={
                          lockDateRepeatControls
                            ? undefined
                            : (e) => updateField('endDate', e.target.value)
                        }
                        min={formState.startDate}
                        aria-label={text(
                          'schedule.modal.override.endDateAriaLabel',
                          'End date',
                        )}
                        px={2}
                        w="140px"
                        flexShrink={0}
                        {...scheduleModalInputStyles}
                        {...scheduleModalInvalidInputStyles}
                        {...getFieldErrorProps('endDate')}
                        {...lockedDateInputProps}
                      />
                    </>
                  )}
                </HStack>
              ) : (
                /* Set Closed Hours: inline date and time inputs */
                <HStack gap={2} align="center">
                  <Text style={scheduleModalLabelStyles} flexShrink={0}>
                    {text('schedule.common.closed', 'Closed')}
                  </Text>
                  <Input
                    ref={startDateInputRef}
                    type="date"
                    value={formState.startDate}
                    onClick={
                      lockDateRepeatControls
                        ? undefined
                        : handlePickerInputClick
                    }
                    onChange={
                      lockDateRepeatControls
                        ? undefined
                        : (e) => handleStartDateChange(e.target.value)
                    }
                    aria-label={text(
                      'schedule.modal.override.startDateAriaLabel',
                      'Start date',
                    )}
                    px={2}
                    w="140px"
                    flexShrink={0}
                    {...scheduleModalInputStyles}
                    {...scheduleModalInvalidInputStyles}
                    {...getFieldErrorProps('startDate')}
                    {...lockedDateInputProps}
                  />
                  {formState.dateType === 'SINGLE_DAY' && (
                    <Text style={supportingTextStyles} flexShrink={0}>
                      {text('schedule.common.from', 'from')}
                    </Text>
                  )}
                  <TimeTextInput
                    ariaLabel={text(
                      'schedule.modal.override.startTimeAriaLabel',
                      'From time',
                    )}
                    placeholder={text(
                      'schedule.modal.override.timePlaceholder',
                      'h:mm',
                    )}
                    dataScheduleTimeInput="start"
                    ref={startTimeInputRef}
                    invalid={fieldError?.field === 'startTime'}
                    describedBy={
                      fieldError?.field === 'startTime'
                        ? fieldErrorId
                        : undefined
                    }
                    value={formState.startTime || '9:00 AM'}
                    onChange={(value) => updateField('startTime', value)}
                  />
                  <Text style={supportingTextStyles} flexShrink={0}>
                    {text('schedule.common.to', 'to')}
                  </Text>
                  {formState.dateType === 'MULTIPLE_DAYS' && (
                    <Input
                      ref={endDateInputRef}
                      type="date"
                      value={formState.endDate || formState.startDate}
                      onClick={
                        lockDateRepeatControls
                          ? undefined
                          : handlePickerInputClick
                      }
                      onChange={
                        lockDateRepeatControls
                          ? undefined
                          : (e) => updateField('endDate', e.target.value)
                      }
                      min={formState.startDate}
                      aria-label={text(
                        'schedule.modal.override.endDateAriaLabel',
                        'End date',
                      )}
                      px={2}
                      w="140px"
                      flexShrink={0}
                      {...scheduleModalInputStyles}
                      {...scheduleModalInvalidInputStyles}
                      {...getFieldErrorProps('endDate')}
                      {...lockedDateInputProps}
                    />
                  )}
                  <TimeTextInput
                    ariaLabel={text(
                      'schedule.modal.override.endTimeAriaLabel',
                      'To time',
                    )}
                    placeholder={text(
                      'schedule.modal.override.timePlaceholder',
                      'h:mm',
                    )}
                    dataScheduleTimeInput="end"
                    ref={endTimeInputRef}
                    invalid={fieldError?.field === 'endTime'}
                    describedBy={
                      fieldError?.field === 'endTime' ? fieldErrorId : undefined
                    }
                    value={formState.endTime || '5:00 PM'}
                    onChange={(value) => updateField('endTime', value)}
                  />
                </HStack>
              )}
              {renderFieldError([
                'startDate',
                'endDate',
                'startTime',
                'endTime',
              ])}

              {lockDateRepeatControls ? (
                <Box pt={2}>
                  {hasIrregularRepeat ? (
                    <>
                      <Text
                        style={{
                          ...supportingTextStyles,
                          fontStyle: 'italic',
                          color: tokenVar('blue.fg'),
                        }}
                      >
                        {text(
                          'schedule.modal.override.irregularRepeatNamed',
                          '{{name}} has a repeat pattern that cannot be edited.',
                          { name: irregularRepeatName },
                        )}
                      </Text>
                      {irregularRepeatNextOccurrence && (
                        <Text
                          mt={1}
                          style={{
                            ...supportingTextStyles,
                            fontStyle: 'italic',
                          }}
                        >
                          {text(
                            'schedule.modal.override.irregularRepeatNextOccurrence',
                            'Next occurrence is {{occurrence}}',
                            { occurrence: irregularRepeatNextOccurrence },
                          )}
                        </Text>
                      )}
                    </>
                  ) : (
                    <Text
                      style={{
                        ...supportingTextStyles,
                        fontStyle: 'italic',
                        color: tokenVar('blue.fg'),
                      }}
                    >
                      {holidayTemplateLockMessage}
                    </Text>
                  )}
                </Box>
              ) : (
                /* Repeat section */
                <Box pt={2}>
                  <HStack gap={1} align="center">
                    <Text style={scheduleModalLabelStyles} flexShrink={0}>
                      {text('schedule.modal.override.repeat', 'Repeat')}
                    </Text>
                    <Box flex={1}>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          ref={repeatFrequencyRef}
                          aria-label={text(
                            'schedule.modal.override.repeat',
                            'Repeat',
                          )}
                          value={formState.repeat.frequency}
                          onChange={(e) =>
                            handleFrequencyChange(
                              e.target
                                .value as OverrideFormState['repeat']['frequency'],
                            )
                          }
                          px={3}
                          {...scheduleModalInputStyles}
                          {...scheduleModalInvalidInputStyles}
                          {...getFieldErrorProps('repeatFrequency')}
                        >
                          <option value="NONE">
                            {text('schedule.common.never', 'Never')}
                          </option>
                          <option value="DAILY">
                            {text('schedule.common.daily', 'Daily')}
                          </option>
                          <option value="WEEKLY">
                            {text('schedule.common.weekly', 'Weekly')}
                          </option>
                          <option value="MONTHLY">
                            {text('schedule.common.monthly', 'Monthly')}
                          </option>
                          <option value="YEARLY">
                            {text('schedule.common.yearly', 'Yearly')}
                          </option>
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Box>
                  </HStack>
                  {renderFieldError(['repeatFrequency'])}

                  {formState.repeat.frequency !== 'NONE' && (
                    <VStack gap={4} align="stretch" mt={4}>
                      {/* Repeat Every */}
                      <HStack gap={2} align="center">
                        <Text style={scheduleModalLabelStyles}>
                          {text(
                            'schedule.modal.override.repeatEvery',
                            'Repeat Every',
                          )}
                        </Text>
                        <Input
                          type="number"
                          min={1}
                          value={formState.repeat.interval}
                          onChange={(e) =>
                            updateRepeatField(
                              'interval',
                              parseInt(e.target.value, 10) || 1,
                            )
                          }
                          w="80px"
                          px={3}
                          {...scheduleModalInputStyles}
                        />
                        <Text style={scheduleModalLabelStyles}>
                          {formState.repeat.frequency === 'DAILY' &&
                            text(
                              'schedule.modal.override.repeatUnit.days',
                              'Day(s)',
                            )}
                          {formState.repeat.frequency === 'WEEKLY' &&
                            text(
                              'schedule.modal.override.repeatUnit.weeks',
                              'Week(s)',
                            )}
                          {formState.repeat.frequency === 'MONTHLY' &&
                            text(
                              'schedule.modal.override.repeatUnit.months',
                              'Month(s)',
                            )}
                          {formState.repeat.frequency === 'YEARLY' &&
                            text(
                              'schedule.modal.override.repeatUnit.years',
                              'Year(s)',
                            )}
                        </Text>
                      </HStack>

                      {/* Weekly specific options */}
                      {formState.repeat.frequency === 'WEEKLY' && (
                        <HStack gap={2} align="center" flexWrap="wrap">
                          <Text style={scheduleModalLabelStyles}>
                            {text(
                              'schedule.modal.override.repeatOn',
                              'Repeat On',
                            )}
                          </Text>
                          {[
                            {
                              value: 0,
                              label: text(
                                'schedule.common.weekdayShort.sun',
                                'Sun',
                              ),
                            },
                            {
                              value: 1,
                              label: text(
                                'schedule.common.weekdayShort.mon',
                                'Mon',
                              ),
                            },
                            {
                              value: 2,
                              label: text(
                                'schedule.common.weekdayShort.tue',
                                'Tue',
                              ),
                            },
                            {
                              value: 3,
                              label: text(
                                'schedule.common.weekdayShort.wed',
                                'Wed',
                              ),
                            },
                            {
                              value: 4,
                              label: text(
                                'schedule.common.weekdayShort.thu',
                                'Thu',
                              ),
                            },
                            {
                              value: 5,
                              label: text(
                                'schedule.common.weekdayShort.fri',
                                'Fri',
                              ),
                            },
                            {
                              value: 6,
                              label: text(
                                'schedule.common.weekdayShort.sat',
                                'Sat',
                              ),
                            },
                          ].map(({ value, label }, index) => (
                            <Checkbox
                              key={value}
                              ref={
                                index === 0 ? firstWeekdayInputRef : undefined
                              }
                              inputProps={getFieldErrorProps('repeatWeekdays')}
                              checked={formState.repeat.byweekday?.includes(
                                value,
                              )}
                              onCheckedChange={(details) =>
                                handleWeekdayToggle(value, !!details.checked)
                              }
                              display="inline-flex"
                              alignItems="center"
                              gap={0}
                            >
                              <Text
                                as="span"
                                style={radioLabelStyles}
                                display="inline-flex"
                                alignItems="center"
                              >
                                {label}
                              </Text>
                            </Checkbox>
                          ))}
                        </HStack>
                      )}
                      {renderFieldError(['repeatWeekdays'])}

                      {/* Monthly specific options */}
                      {formState.repeat.frequency === 'MONTHLY' &&
                        (() => {
                          // Derive values from start date
                          const [year, month, day] = formState.startDate
                            .split('-')
                            .map(Number)
                          const date = new Date(year, month - 1, day)
                          const dayOfMonth = day
                          const weekdayIndex = date.getDay() // 0=Sunday, 6=Saturday
                          const nthOccurrence = Math.ceil(day / 7)

                          return (
                            <HStack gap={2} align="center">
                              <Text style={scheduleModalLabelStyles}>
                                {text(
                                  'schedule.modal.override.repeatOn',
                                  'Repeat On',
                                )}
                              </Text>
                              <RadioGroup
                                value={
                                  formState.repeat.monthlyType ||
                                  'SPECIFIC_DATE'
                                }
                                onValueChange={(details) =>
                                  updateRepeatField(
                                    'monthlyType',
                                    details.value as
                                      'SPECIFIC_DATE' | 'NTH_WEEKDAY',
                                  )
                                }
                              >
                                <HStack gap={4}>
                                  <Radio
                                    value="SPECIFIC_DATE"
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Text as="span" style={radioLabelStyles}>
                                      {text(
                                        'schedule.modal.override.monthly.specificDate',
                                        'The {{day}}{{suffix}}',
                                        {
                                          day: dayOfMonth,
                                          suffix: getOrdinalSuffix(dayOfMonth),
                                        },
                                      )}
                                    </Text>
                                  </Radio>
                                  <Radio
                                    value="NTH_WEEKDAY"
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Text as="span" style={radioLabelStyles}>
                                      {text(
                                        'schedule.modal.override.monthly.nthWeekday',
                                        'The {{nth}}{{suffix}} {{weekday}}',
                                        {
                                          nth: nthOccurrence,
                                          suffix:
                                            getOrdinalSuffix(nthOccurrence),
                                          weekday: getWeekdayName(weekdayIndex),
                                        },
                                      )}
                                    </Text>
                                  </Radio>
                                </HStack>
                              </RadioGroup>
                            </HStack>
                          )
                        })()}

                      {/* Yearly specific options */}
                      {formState.repeat.frequency === 'YEARLY' &&
                        (() => {
                          // Derive values from start date
                          const [year, month, day] = formState.startDate
                            .split('-')
                            .map(Number)
                          const date = new Date(year, month - 1, day)
                          const dayOfMonth = day
                          const monthName = getMonthName(month)
                          const weekdayIndex = date.getDay() // 0=Sunday, 6=Saturday
                          const nthOccurrence = Math.ceil(day / 7)

                          return (
                            <HStack gap={2} align="center">
                              <Text style={scheduleModalLabelStyles}>
                                {text(
                                  'schedule.modal.override.repeatOn',
                                  'Repeat On',
                                )}
                              </Text>
                              <RadioGroup
                                value={formState.repeat.yearlyType}
                                onValueChange={(details) =>
                                  updateRepeatField(
                                    'yearlyType',
                                    details.value as
                                      'SPECIFIC_DATE' | 'NTH_WEEKDAY',
                                  )
                                }
                              >
                                <HStack gap={4}>
                                  <Radio
                                    value="SPECIFIC_DATE"
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Text as="span" style={radioLabelStyles}>
                                      {text(
                                        'schedule.modal.override.yearly.specificDate',
                                        'The {{day}}{{suffix}} of {{month}}',
                                        {
                                          day: dayOfMonth,
                                          suffix: getOrdinalSuffix(dayOfMonth),
                                          month: monthName,
                                        },
                                      )}
                                    </Text>
                                  </Radio>
                                  <Radio
                                    value="NTH_WEEKDAY"
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Text as="span" style={radioLabelStyles}>
                                      {text(
                                        'schedule.modal.override.yearly.nthWeekday',
                                        'The {{nth}}{{suffix}} {{weekday}} of {{month}}',
                                        {
                                          nth: nthOccurrence,
                                          suffix:
                                            getOrdinalSuffix(nthOccurrence),
                                          weekday: getWeekdayName(weekdayIndex),
                                          month: monthName,
                                        },
                                      )}
                                    </Text>
                                  </Radio>
                                </HStack>
                              </RadioGroup>
                            </HStack>
                          )
                        })()}

                      {/* End Repeat */}
                      <HStack gap={2} align="center">
                        <Text style={scheduleModalLabelStyles} minW="80px">
                          {text(
                            'schedule.modal.override.endRepeat',
                            'End Repeat',
                          )}
                        </Text>
                        <NativeSelect.Root w="120px">
                          <NativeSelect.Field
                            value={formState.repeat.endType}
                            onChange={(e) =>
                              updateRepeatField(
                                'endType',
                                e.target
                                  .value as OverrideFormState['repeat']['endType'],
                              )
                            }
                            px={3}
                            {...scheduleModalInputStyles}
                          >
                            <option value="NEVER">
                              {text('schedule.common.never', 'Never')}
                            </option>
                            <option value="AFTER">
                              {text('schedule.common.after', 'After')}
                            </option>
                            <option value="ON_DATE">
                              {text('schedule.common.onDate', 'On Date')}
                            </option>
                          </NativeSelect.Field>
                        </NativeSelect.Root>

                        {formState.repeat.endType === 'AFTER' && (
                          <>
                            <Input
                              ref={repeatCountInputRef}
                              aria-label={text(
                                'schedule.modal.override.occurrences',
                                'occurrences',
                              )}
                              {...getFieldErrorProps('repeatCount')}
                              type="number"
                              min={1}
                              value={formState.repeat.count || 1}
                              onChange={(e) =>
                                updateRepeatField(
                                  'count',
                                  parseInt(e.target.value, 10) || 1,
                                )
                              }
                              w="80px"
                              px={3}
                              {...scheduleModalInputStyles}
                              {...scheduleModalInvalidInputStyles}
                            />
                            <Text style={supportingTextStyles}>
                              {text(
                                'schedule.modal.override.occurrences',
                                'occurrences',
                              )}
                            </Text>
                          </>
                        )}
                        {formState.repeat.endType === 'ON_DATE' && (
                          <Input
                            ref={repeatUntilInputRef}
                            aria-label={text(
                              'schedule.modal.override.endRepeatDateAriaLabel',
                              'End repeat date',
                            )}
                            {...getFieldErrorProps('repeatUntil')}
                            type="date"
                            value={formState.repeat.until || ''}
                            onClick={handlePickerInputClick}
                            onChange={(e) =>
                              updateRepeatField('until', e.target.value)
                            }
                            w="160px"
                            px={3}
                            {...scheduleModalInputStyles}
                            {...scheduleModalInvalidInputStyles}
                          />
                        )}
                      </HStack>
                      {renderFieldError(['repeatCount', 'repeatUntil'])}
                    </VStack>
                  )}
                </Box>
              )}
            </VStack>
          </Box>

          <Separator borderColor={tokenVar('border.default')} />

          {/* Routing */}
          <HStack justify="space-between" align="center">
            <Text style={sectionHeadingStyles}>
              {text('schedule.common.routing', 'Routing')}
            </Text>
            <RadioGroup
              value={formState.routing}
              onValueChange={(details) =>
                updateField('routing', details.value as 'CLOSED' | 'HOLIDAY')
              }
            >
              <HStack gap={6}>
                <Radio value="CLOSED" display="flex" alignItems="center">
                  <Text
                    as="span"
                    display="inline-block"
                    w="100px"
                    style={radioLabelStyles}
                  >
                    {text('schedule.common.closed', 'Closed')}
                  </Text>
                </Radio>
                <Radio value="HOLIDAY" display="flex" alignItems="center">
                  <Text
                    as="span"
                    display="inline-block"
                    w="125px"
                    style={radioLabelStyles}
                  >
                    {text('schedule.common.holiday', 'Holiday')}
                  </Text>
                </Radio>
              </HStack>
            </RadioGroup>
          </HStack>
        </VStack>
      )}
    </Modal>
  )
}
